import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { syncOneSignalUserIdToProfile } from '@/lib/onesignal';
import { isDemoEmail } from '@/lib/demo-mode';
import { isDefinitiveAuthFailure } from '@/lib/auth-recovery';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  companyId: string | null;
  userRole: string | null;
  loading: boolean;
  isDemoAccount: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: any; user: User | null }>;
  refreshSession: () => Promise<void>;
  isSessionValid: () => boolean;
  forceLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  companyId: null,
  userRole: null,
  loading: true,
  isDemoAccount: false,
  signOut: async () => {},
  signIn: async () => ({ error: null, user: null }),
  refreshSession: async () => {},
  isSessionValid: () => false,
  forceLogout: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  console.log('AuthProvider render');
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastSessionCheck, setLastSessionCheck] = useState<Date | null>(null);
  const [lastOneSignalSyncedUserId, setLastOneSignalSyncedUserId] = useState<string | null>(null);

  // Check if session is still valid
  const isSessionValid = () => {
    if (!session) return false;
    
    // Check if session is expired
    const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
    const now = new Date();
    
    if (expiresAt && now >= expiresAt) {
      console.warn('Session has expired');
      return false;
    }

    return true;
  };

  /**
   * True when the access token expires within 10 minutes. The keep-alive ticks
   * every 5 minutes, so renewing inside this window means the token is always
   * replaced before it can lapse — important on a kiosk nobody is watching.
   */
  const isSessionExpiringSoon = () => {
    if (!session?.expires_at) return false;
    return session.expires_at * 1000 - Date.now() < 10 * 60 * 1000;
  };

  // Refresh session manually
  const refreshSession = async () => {
    try {
      console.log('Refreshing session...');
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        throw error;
      }
      
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
        setLastSessionCheck(new Date());
        console.log('Session refreshed successfully');
      }
      
      return data;
    } catch (error) {
      console.error('Failed to refresh session:', error);
      throw error;
    }
  };

  // Periodic session health check and keep-alive
  useEffect(() => {
    if (!session || !user) return;
    
    // Counts consecutive failed refreshes so we can back off instead of
    // hammering the server while the network is down.
    let refreshFailures = 0;

    const keepAliveInterval = setInterval(() => {
      (async () => {
        // Nothing here can succeed while offline, and every failure would just
        // be a false alarm. Wait for the connection to come back.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          console.log('Keep-alive skipped: device is offline');
          return;
        }

        try {
          // Refresh proactively once the token is close to expiring, so the
          // session is renewed before anything starts failing.
          if (!isSessionValid() || isSessionExpiringSoon()) {
            try {
              await refreshSession();
              refreshFailures = 0;
            } catch (refreshError) {
              // Only a server-rejected refresh token is fatal. Anything else
              // (offline, DNS, 5xx) gets retried on a later tick.
              if (isDefinitiveAuthFailure(refreshError)) {
                console.error('Refresh token rejected, forcing logout:', refreshError);
                await forceLogout();
                return;
              }
              refreshFailures += 1;
              console.warn(
                `Session refresh failed transiently (attempt ${refreshFailures}), will retry:`,
                refreshError
              );
              return;
            }
          }

          // Lightweight ping to keep the connection warm. A failure here is a
          // network/RLS problem, never a reason to destroy the session.
          const { error } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', user.id)
            .limit(1);

          if (error) {
            console.warn('Keep-alive ping failed (session kept):', error);
          } else {
            setLastSessionCheck(new Date());
          }
        } catch (error) {
          console.warn('Error in keep-alive ping (session kept):', error);
        }
      })();
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(keepAliveInterval);
  }, [session, user]);

  /**
   * Recover the session as soon as the device becomes usable again.
   *
   * An unattended kiosk can sit backgrounded or offline for hours; timers are
   * throttled or frozen while that happens, so the 5-minute keep-alive may not
   * have run. Refresh on wake and on reconnect so the app is ready the moment
   * someone walks up to it, rather than failing on their first tap.
   */
  useEffect(() => {
    if (!session || !user) return;

    let recovering = false;

    const recover = async (reason: string) => {
      if (recovering) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (isSessionValid() && !isSessionExpiringSoon()) return;

      recovering = true;
      try {
        console.log(`Recovering session after ${reason}`);
        await refreshSession();
      } catch (error) {
        if (isDefinitiveAuthFailure(error)) {
          console.error('Refresh token rejected during recovery, forcing logout:', error);
          await forceLogout();
        } else {
          console.warn('Session recovery failed transiently, will retry later:', error);
        }
      } finally {
        recovering = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void recover('app resume');
    };
    const onOnline = () => void recover('network reconnect');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onVisibility);
    };
  }, [session, user]);

  // Add safety timeout to ensure loading never stays true indefinitely
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.warn('Auth loading timeout reached, forcing loading to false');
        setLoading(false);
      }
    }, 15000); // 15 second safety timeout

    return () => clearTimeout(safetyTimeout);
  }, [loading]);

  useEffect(() => {
    console.log('=== useAuth useEffect: Starting auth initialization ===');
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('=== getSession result ===', { session: session ? 'exists' : 'null', error });
      
      if (error) {
        console.error('Error getting session:', error);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Get user profile to get company_id
        console.log('Initial session found, fetching user profile for:', session.user.id);
        void fetchUserProfile(session.user.id);
      } else {
        console.log('No initial session found');
        setLoading(false);
      }
    }).catch((error) => {
      console.error('Error in getSession:', error);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session ? 'session exists' : 'no session');
      
      // Handle different auth events
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
        console.log('User signed out or token refresh failed, clearing state');
        setSession(null);
        setUser(null);
        setCompanyId(null);
        setUserRole(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED' && session) {
        console.log('Token refreshed, session extended');
      }
      
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in, setting up session');
        setSession(session);
        setUser(session.user);
        void fetchUserProfile(session.user.id);
        return;
      }
      
      // For other events, validate session before proceeding
      if (session) {
        // Check if the session is valid
        const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
        const now = new Date();

        // An expired access token is the normal case after the device has been
        // idle — that's what the refresh token is for. Only give up if the
        // server actually rejects the refresh; never on a network failure,
        // which would strand an unattended kiosk at the login screen.
        if (expiresAt && now >= expiresAt) {
          console.warn('Received expired session, attempting refresh');
          try {
            const { data, error } = await supabase.auth.refreshSession();
            if (error) throw error;
            if (data.session) {
              setSession(data.session);
              setUser(data.session.user);
              void fetchUserProfile(data.session.user.id);
              return;
            }
            return;
          } catch (refreshError) {
            if (isDefinitiveAuthFailure(refreshError)) {
              console.error('Refresh token rejected, forcing logout:', refreshError);
              await forceLogout();
            } else {
              console.warn('Refresh failed transiently, keeping session:', refreshError);
            }
            return;
          }
        }

        setSession(session);
        setUser(session.user);
        
        if (session.user) {
          console.log('Auth state change - fetching user profile for:', session.user.id);
          void fetchUserProfile(session.user.id);
        }
      } else {
        console.log('Auth state change - no session, clearing state');
        setSession(null);
        setUser(null);
        setCompanyId(null);
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Ensure OneSignal sync also runs for restored sessions (not only manual sign-in).
  useEffect(() => {
    if (!user?.id) return;
    if (lastOneSignalSyncedUserId === user.id) return;

    console.log('[Auth] Triggering OneSignal sync for user:', user.id);
    setLastOneSignalSyncedUserId(user.id);
    void syncOneSignalUserIdToProfile(user.id);
  }, [user?.id, lastOneSignalSyncedUserId]);

  useEffect(() => {
    if (!user) {
      setLastOneSignalSyncedUserId(null);
    }
  }, [user]);

  const fetchUserProfile = async (userId: string, retryCount = 0) => {
    console.log(`=== fetchUserProfile Debug (attempt ${retryCount + 1}) ===`);
    console.log('userId:', userId);
    
    const maxRetries = 3;
    const retryDelay = 1000 * (retryCount + 1); // Exponential backoff
    
    try {
      // Shorter timeout for better UX
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
      );

      console.log('Querying user_profiles table...');
      const profilePromise = supabase
        .from('user_profiles')
        .select('company_id, role')
        .eq('id', userId)
        .single();

      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]) as any;

      console.log('Profile query result:', { profile, error });

      if (error) {
        console.error(`Error fetching user profile (attempt ${retryCount + 1}):`, error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        // If it's a network/timeout error and we have retries left, try again
        if (retryCount < maxRetries && (
          error.message.includes('timeout') || 
          error.message.includes('network') ||
          error.code === 'ENOTFOUND' ||
          error.code === 'ETIMEDOUT'
        )) {
          console.log(`Retrying profile fetch in ${retryDelay}ms...`);
          setTimeout(() => {
            fetchUserProfile(userId, retryCount + 1);
          }, retryDelay);
          return;
        }
        
        // Set null on permanent errors but don't fail auth completely
        console.log('Setting companyId to null due to profile fetch error');
        setCompanyId(null);
        setUserRole(null);
      } else {
        console.log('Profile data received:', profile);
        console.log('company_id from profile:', profile?.company_id);
        console.log('About to call setCompanyId with:', profile?.company_id || null);
        setCompanyId(profile?.company_id || null);
        setUserRole(profile?.role || null);
        console.log('setCompanyId called');
        
        // Add a timeout to check if state was actually updated
        setTimeout(() => {
          console.log('State check after setCompanyId - current companyId in closure should be:', profile?.company_id || null);
        }, 100);
        
        console.log('User profile fetched successfully:', profile?.company_id);
      }
    } catch (error) {
      console.error(`Error in fetchUserProfile (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for catch block
      if (retryCount < maxRetries) {
        console.log(`Retrying profile fetch in ${retryDelay}ms...`);
        setTimeout(() => {
          fetchUserProfile(userId, retryCount + 1);
        }, retryDelay);
        return;
      }
      
      // Final fallback - set company ID to null but don't break auth
      setCompanyId(null);
    } finally {
      // Only set loading to false on final attempt or success
      if (retryCount === 0 || retryCount >= maxRetries) {
        setLoading(false);
      }
    }
  };

  const signOut = async () => {
    try {
      // Clear local state first to ensure immediate logout
      setUser(null);
      setSession(null);
      setCompanyId(null);
      setUserRole(null);
      
      // Try to sign out from Supabase, but don't fail if session is missing
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Error signing out:', error);
        
        // If it's a session missing error, we still want to clear everything locally
        if (error.message?.includes('Auth session missing') || error.message?.includes('session missing')) {
          console.log('Session was already missing, clearing local state and redirecting to login');
          // Force clear any remaining auth data
          await supabase.auth.signOut({ scope: 'local' });
          return; // Don't throw error, just return successfully
        }
        
        // For other errors, still clear local state but log the error
        console.warn('Sign out had an error but local state has been cleared:', error);
        return; // Don't throw error to prevent UI issues
      }
      
      console.log('Successfully signed out');
    } catch (error) {
      console.error('Unexpected error during sign out:', error);
      // Even if there's an unexpected error, ensure local state is cleared
      setUser(null);
      setSession(null);
      setCompanyId(null);
      setUserRole(null);
      
      // Try local sign out as fallback
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (localError) {
        console.error('Local sign out also failed:', localError);
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error && data.user) {
      void syncOneSignalUserIdToProfile(data.user.id);
    }

    return { error, user: data.user };
  };

  // Force logout when session is invalid or missing
  const forceLogout = async () => {
    console.log('Force logout initiated - clearing all auth state');

    // Clear all local state immediately
    setUser(null);
    setSession(null);
    setCompanyId(null);
    setUserRole(null);
    setLoading(false);
    
    try {
      // Clear local storage and session data
      await supabase.auth.signOut({ scope: 'local' });
      
      // Clear any remaining local storage items that might be auth-related
      if (typeof window !== 'undefined') {
        // Clear Supabase auth tokens from local storage
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('supabase.auth.') || key.includes('auth-token'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Also clear session storage
        sessionStorage.clear();
      }
      
      console.log('Force logout completed - all auth data cleared');
    } catch (error) {
      console.error('Error during force logout:', error);
      // Even if clearing fails, we've already cleared local state
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      companyId,
      userRole,
      loading,
      isDemoAccount: isDemoEmail(user?.email),
      signOut,
      signIn,
      refreshSession,
      isSessionValid,
      forceLogout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
